// What the model is asked to read off a contractor's invoice PDF, and the exact
// shape it must answer in (CINV-7: the raw answer is stored immutably).
//
// Two representations of one schema live here on purpose:
//
//   `invoiceExtractionSchema`        zod — parses the model's reply.
//   `INVOICE_EXTRACTION_JSON_SCHEMA` hand-written JSON Schema — what goes on the
//                                    wire as `response_format.json_schema.schema`.
//
// Strict structured outputs are picky: every property listed in `required`,
// `additionalProperties: false` on every object, and nullability as a type array
// rather than the `anyOf` zod's converter emits. So the wire schema is written
// by hand in the conservative dialect, and `test/invoice-extraction.test.ts`
// pins the two together — a field added to one and not the other is a failing
// test, not a silent null.
//
// Wire discipline, enforced by that test: only strings and numbers are nullable;
// every enum carries an `UNKNOWN` member instead of admitting null; nothing is
// `.optional()`, `.min()`, `.email()`, `.date()` or `.regex()` — those become
// JSON-Schema keywords some providers reject outright. Dates are plain strings the
// normaliser validates.
//
// Every object is `z.strictObject`, matching `additionalProperties: false` on the
// wire. A reply that adds a key (a `bsb`, an `accountNumber`) is refused, not
// trimmed: it failed the instruction, and the raw text is what gets stored
// (CINV-7, CINV-14), so a parse that dropped the key would still keep it.

import { z } from "zod";
import {
  EXPENSE_CATEGORIES,
  EXTRACTED_CURRENCIES,
  EXTRACTED_UNITS,
  INVOICE_DOCUMENT_KINDS,
  type ExpenseCategory,
} from "./constants.js";

const text = z.string().nullable();
const num = z.number().nullable();
/** 0..1 by instruction; clamped by the normaliser, never by the schema. */
const score = z.number();

export const extractedLineItemSchema = z.strictObject({
  description: text,
  quantity: num,
  unit: z.enum(EXTRACTED_UNITS),
  unitRate: num,
  amount: num,
  projectOrClientHint: text,
  isExpense: z.boolean(),
});
export type ExtractedLineItem = z.infer<typeof extractedLineItemSchema>;

/**
 * What every read shares — the contractor's and the expense's. The expense read
 * is this plus `expenseCategory` and nothing else, byte for byte what it was
 * before sources existed (a test pins it); the contractor read adds where each
 * figure is printed (CINV-30).
 */
export const baseExtractionSchema = z.strictObject({
  isInvoice: z.boolean(),
  documentKind: z.enum(INVOICE_DOCUMENT_KINDS),
  supplier: z.strictObject({
    name: text,
    abn: text,
    gstin: text,
    email: text,
    address: text,
  }),
  billToName: text,
  invoiceNumber: text,
  description: text,
  invoiceDate: text,
  dueDate: text,
  periodStart: text,
  periodEnd: text,
  currency: z.enum(EXTRACTED_CURRENCIES),
  lineItems: z.array(extractedLineItemSchema),
  subtotal: num,
  taxLabel: text,
  taxRatePct: num,
  taxAmount: num,
  total: num,
  /**
   * Presence only, never the numbers (CINV-14). The portal does not pay, so an
   * account number here would be a liability with no user — and the raw answer
   * is stored immutably, which would make this table the place to steal it from.
   */
  bankDetailsPresent: z.boolean(),
  paymentReference: text,
  confidence: z.strictObject({
    document: score,
    supplier: score,
    header: score,
    lineItems: score,
    totals: score,
  }),
  warnings: z.array(z.string()),
});
/** The fields every engine reads. A contractor read carries more (`ContractorInvoiceExtraction`). */
export type InvoiceExtraction = z.infer<typeof baseExtractionSchema>;

/** Where one figure is printed: the text as it appears, and its 1-based page. Both null when the field is. */
const sourceSchema = z.strictObject({ quote: text, page: num });

/** The fields a contractor read quotes, so an admin can see each on the PDF (CINV-30). */
export const SOURCED_FIELDS = [
  "invoiceNumber",
  "invoiceDate",
  "period",
  "supplierName",
  "supplierTaxId",
  "currency",
  "subtotal",
  "taxAmount",
  "total",
] as const;
export type SourcedField = (typeof SOURCED_FIELDS)[number];

export const sourcedLineItemSchema = extractedLineItemSchema.extend({ quote: text, page: num });

/** The contractor read: the shared fields, and where each is printed (CINV-30). */
export const invoiceExtractionSchema = baseExtractionSchema.extend({
  lineItems: z.array(sourcedLineItemSchema),
  sources: z.strictObject(
    Object.fromEntries(SOURCED_FIELDS.map((f) => [f, sourceSchema])) as Record<SourcedField, typeof sourceSchema>
  ),
});
export type ContractorInvoiceExtraction = z.infer<typeof invoiceExtractionSchema>;
export type ExtractionSource = z.infer<typeof sourceSchema>;

/** A read with no sources — what every reply stored before them is read as. */
export const EMPTY_SOURCES: ContractorInvoiceExtraction["sources"] = Object.fromEntries(
  SOURCED_FIELDS.map((f) => [f, { quote: null, page: null }])
) as ContractorInvoiceExtraction["sources"];

/** The `name` OpenRouter wants on a `json_schema` response format. */
export const INVOICE_EXTRACTION_SCHEMA_NAME = "contractor_invoice_extraction";

const nullableString = (description?: string) => ({
  type: ["string", "null"] as const,
  ...(description ? { description } : {}),
});
const nullableNumber = (description?: string) => ({
  type: ["number", "null"] as const,
  ...(description ? { description } : {}),
});

/**
 * The wire schema every read shares. Kept as a plain object literal so it can
 * be read as a document; the shape test is what keeps it honest against the
 * zod above.
 */
export const BASE_EXTRACTION_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    isInvoice: {
      type: "boolean",
      description: "True only if the document is an invoice, tax invoice or bill.",
    },
    documentKind: {
      type: "string",
      enum: [...INVOICE_DOCUMENT_KINDS],
      description: "What kind of document this is.",
    },
    supplier: {
      type: "object",
      additionalProperties: false,
      properties: {
        name: nullableString("Issuer's trading or legal name as printed."),
        abn: nullableString("Australian Business Number, digits only, else null."),
        gstin: nullableString("Indian GSTIN as printed without spaces, else null."),
        email: nullableString(),
        address: nullableString("Postal address on one line, else null."),
      },
      required: ["name", "abn", "gstin", "email", "address"],
    },
    billToName: nullableString("The party the invoice is addressed to."),
    invoiceNumber: nullableString(),
    description: nullableString(
      "What the invoice says it is for, as printed in its header, subject or reference — not a line item's text."
    ),
    invoiceDate: nullableString("YYYY-MM-DD."),
    dueDate: nullableString("YYYY-MM-DD."),
    periodStart: nullableString("YYYY-MM-DD first day of the service period, else null."),
    periodEnd: nullableString("YYYY-MM-DD last day of the service period, else null."),
    currency: { type: "string", enum: [...EXTRACTED_CURRENCIES] },
    lineItems: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          description: nullableString(),
          quantity: nullableNumber("The number in the qty, days or hours column."),
          unit: { type: "string", enum: [...EXTRACTED_UNITS] },
          unitRate: nullableNumber("Price per unit before tax."),
          amount: nullableNumber("Row total before tax."),
          projectOrClientHint: nullableString(
            "Project, client, site or work-order named for this row."
          ),
          isExpense: {
            type: "boolean",
            description:
              "True for reimbursements, travel, accommodation, materials, software, per-diem.",
          },
        },
        required: [
          "description",
          "quantity",
          "unit",
          "unitRate",
          "amount",
          "projectOrClientHint",
          "isExpense",
        ],
      },
    },
    subtotal: nullableNumber("Pre-tax total as printed."),
    taxLabel: nullableString("Printed tax label, e.g. 'GST 10%' or 'CGST 9% + SGST 9%'."),
    taxRatePct: nullableNumber(
      "Printed tax rate as a number: 10 for 10%. Sum of split rates."
    ),
    taxAmount: nullableNumber("Tax as printed. Sum of split components."),
    total: nullableNumber("Amount payable as printed."),
    bankDetailsPresent: {
      type: "boolean",
      description:
        "True if a BSB, account number, IFSC, IBAN, SWIFT or UPI id appears. Do not transcribe them.",
    },
    paymentReference: nullableString(),
    confidence: {
      type: "object",
      additionalProperties: false,
      properties: {
        document: { type: "number" },
        supplier: { type: "number" },
        header: { type: "number" },
        lineItems: { type: "number" },
        totals: { type: "number" },
      },
      required: ["document", "supplier", "header", "lineItems", "totals"],
      description: "0 to 1 per section.",
    },
    warnings: { type: "array", items: { type: "string" } },
  },
  required: [
    "isInvoice",
    "documentKind",
    "supplier",
    "billToName",
    "invoiceNumber",
    "description",
    "invoiceDate",
    "dueDate",
    "periodStart",
    "periodEnd",
    "currency",
    "lineItems",
    "subtotal",
    "taxLabel",
    "taxRatePct",
    "taxAmount",
    "total",
    "bankDetailsPresent",
    "paymentReference",
    "confidence",
    "warnings",
  ],
} as const;

const SOURCE_JSON = {
  type: "object",
  additionalProperties: false,
  properties: {
    quote: nullableString("The text showing the value, exactly as printed. Null when the field is."),
    page: nullableNumber("1-based page it is printed on. Null when the field is."),
  },
  required: ["quote", "page"],
} as const;

/** The contractor read's wire schema: the shared one, with sources (CINV-30). */
export const INVOICE_EXTRACTION_JSON_SCHEMA = {
  ...BASE_EXTRACTION_JSON_SCHEMA,
  properties: {
    ...BASE_EXTRACTION_JSON_SCHEMA.properties,
    lineItems: {
      ...BASE_EXTRACTION_JSON_SCHEMA.properties.lineItems,
      items: {
        ...BASE_EXTRACTION_JSON_SCHEMA.properties.lineItems.items,
        properties: {
          ...BASE_EXTRACTION_JSON_SCHEMA.properties.lineItems.items.properties,
          quote: nullableString("The row as printed, at most 120 characters."),
          page: nullableNumber("1-based page the row is on."),
        },
        required: [...BASE_EXTRACTION_JSON_SCHEMA.properties.lineItems.items.required, "quote", "page"],
      },
    },
    sources: {
      type: "object",
      additionalProperties: false,
      description: "Where each figure is printed, so a person can find it on the page.",
      properties: Object.fromEntries(SOURCED_FIELDS.map((f) => [f, SOURCE_JSON])) as Record<
        SourcedField,
        typeof SOURCE_JSON
      >,
      required: [...SOURCED_FIELDS],
    },
  },
  required: [...BASE_EXTRACTION_JSON_SCHEMA.required, "sources"],
} as const;

/**
 * The system prompt.
 *
 * It names no contractor, no month and no expected total. A hinted model infers,
 * and rule 1 forbids inferring: the comparison against what we expected is the
 * validation engine's job, run on what the model *read*, not on what it was told
 * to find.
 */
const BASE_EXTRACTION_PROMPT = `You extract fields from a contractor's invoice (a PDF, possibly scanned) for a timesheet reconciliation system. Respond with a single JSON object matching the provided schema and nothing else.

1. Transcribe, never infer. Return only what the document states. If a field is not printed, return null (or "UNKNOWN" for an enumeration). Do not compute totals, tax, hours or days yourself, and do not fill a field from another field.
2. Dates are ISO YYYY-MM-DD. Australian and Indian documents write day/month/year: 03/05/2026 is 3 May 2026. A service period written as a month ("September 2026", "Sep-26") is that month's first and last day. A range like "27 Apr – 3 May 2026" is periodStart 2026-04-27 and periodEnd 2026-05-03. The period may be printed anywhere on the page, not only in the header: a line's period or details column ("3 Sep – 30 Sep 2026"), or a note ("Billing period 3–30 September 2026"). When the header has none, use those — periodStart is the earliest start printed and periodEnd the latest end — and that is transcription, not inference. If no period is printed anywhere, both are null; never derive one from the invoice date.
3. Numbers are plain JSON numbers: no currency symbols, no thousands separators, decimal point. Percentages as numbers (10 for 10%). Credits are negative.
4. currency comes only from symbols, codes or words on the page: "$", "A$", "AUD" → AUD; "₹", "Rs", "Rs.", "INR" → INR; any other stated currency → OTHER; none stated → UNKNOWN. Do not infer it from the supplier's country or tax number.
5. lineItems has one entry per row of the items table, in order. quantity is the number in the qty, days or hours column. unit is DAY if the column heading or row text says day, days or d; HOUR if hour, hours, hrs or h; OTHER if some other unit is printed (month, lot, fixed, each); UNKNOWN if no unit is printed — do not guess. unitRate is the per-unit price and amount the row total, both before tax. projectOrClientHint is any project, client, site or work-order name in the row or in a heading grouping the rows. isExpense is true for anything billed on top of the work itself: travel, accommodation, per-diem, materials, software and subscriptions, training, certifications and exam fees, and reimbursements; false for the time, labour, consulting and services being invoiced.
6. subtotal is the pre-tax total as printed. taxLabel is the printed label; for split Indian GST combine into one label ("CGST 9% + SGST 9%"), taxRatePct the summed rate (18) and taxAmount the summed tax. total is the amount payable. If the page says "Total includes GST" without a tax line, leave taxAmount null and add a warning. Never subtract TDS or withholding from total; describe any TDS note in warnings.
7. supplier is the issuer: the party whose ABN, GSTIN and bank details appear. abn is 11 digits with spaces removed; gstin is the 15-character code with spaces removed. billToName is the party the invoice is addressed to.
8. bankDetailsPresent is true if a BSB, account number, IFSC, IBAN, SWIFT or UPI id appears. Do not transcribe any of them anywhere in the output.
9. documentKind is one of INVOICE, CREDIT_NOTE, QUOTE, RECEIPT, TIMESHEET, STATEMENT, OTHER or UNKNOWN. isInvoice is true only for INVOICE (including "Tax Invoice" and "Bill").
10. confidence is 0 to 1 per section for how legible and unambiguous that section was. Lower it for scans, handwriting, unlabelled columns, or two candidate values.
11. description is what the invoice as a whole says it is for, as printed in its header, subject, "for" or reference line ("Consulting services — September 2026"). If no such line is printed, return null; never copy a line item's description up into it.
12. warnings are short plain-English notes on anything ambiguous or unusual ("qty column has no heading", "two totals printed", "period spans two months", "TDS 10% note present").`;

/**
 * The contractor read's prompt: the shared rules and rule 13, sources
 * (CINV-30). A source is a quote, never a position — models read pages well and
 * place boxes badly — and never a bank detail (CINV-14).
 */
export const INVOICE_EXTRACTION_PROMPT = `${BASE_EXTRACTION_PROMPT}
13. sources says where each figure is printed, so a person can find it. For each field, quote is the shortest run of text on the page that shows the value, copied exactly as printed — same spelling, punctuation and spacing, at most 100 characters — and page is its 1-based page number. period is the service period; supplierTaxId is the ABN or GSTIN. When the field is null, quote and page are null too. Each line item's quote is its row as printed (at most 120 characters) and page its page. Never quote a BSB, account number, IFSC, IBAN, SWIFT or UPI id: quote less, or null.`;

/**
 * What a stored reply is read as when it predates a key: null, "not read" —
 * exactly what it was. One list for both stored-reply parsers; add a key here
 * whenever one is added to the shared schema, or every older reply stops parsing.
 */
const STORED_EXTRACTION_DEFAULTS = {
  // Added 2026-10 (requirement 6): absent from every reply stored before then.
  description: null,
} as const;

/**
 * The contractor read's additions, as a stored reply that predates them reads:
 * no sources, and no quote on any line (CINV-30). The lines are nested, so they
 * are padded one by one — a top-level default would leave every older reply's
 * lines short of two keys, and the strict parse would refuse them all.
 */
function withStoredSources(value: Record<string, unknown>): Record<string, unknown> {
  const lines = Array.isArray(value.lineItems)
    ? value.lineItems.map((line: unknown) =>
        line && typeof line === "object" ? { quote: null, page: null, ...line } : line
      )
    : value.lineItems;
  return { sources: EMPTY_SOURCES, ...value, lineItems: lines };
}

/**
 * A stored model reply, parsed for reading back — not the worker's check on a
 * fresh one, which stays strict.
 *
 * Replies are stored verbatim and never rewritten (CINV-7), so a key added to
 * the schema after a reply was stored is simply absent from it. Each such key
 * is read as null — "not read", exactly what it was — rather than failing the
 * whole parse, which would make every older invoice's document facts
 * unreadable at once — see `STORED_EXTRACTION_DEFAULTS`.
 */
export function parseStoredExtraction(content: string): ContractorInvoiceExtraction | null {
  let value: unknown;
  try {
    value = JSON.parse(stripJsonFences(content));
  } catch {
    return null;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const parsed = invoiceExtractionSchema.safeParse(
    withStoredSources({ ...STORED_EXTRACTION_DEFAULTS, ...(value as Record<string, unknown>) })
  );
  return parsed.success ? parsed.data : null;
}

/**
 * The prompt for a supplier invoice or receipt an admin records (EXP-6). The
 * contractor prompt with three differences, and only three — the transcription
 * rules and the shared fields' meanings are otherwise the same:
 *
 * - the opener names the document for what it is;
 * - rule 5's `isExpense`. On a contractor invoice it marks what is billed on
 *   top of the work. On a supplier's invoice the licence or the laptop *is* the
 *   purchase: read the contractor way, every line would be "extra", the whole
 *   invoice would land in other charges, and — since any other charge is a
 *   review (CINV-18) — no expense could ever match;
 * - rule 13, `expenseCategory`: the one field that classifies rather than
 *   transcribes. It is only ever a suggestion an admin confirms (EXP-8), so it
 *   is said to be one, lest it loosen rule 1 for the rest of the read.
 */
export const EXPENSE_EXTRACTION_PROMPT =
  BASE_EXTRACTION_PROMPT.replace(
    "You extract fields from a contractor's invoice (a PDF, possibly scanned) for a timesheet reconciliation system.",
    "You extract fields from a supplier's invoice or receipt (a PDF, a scan or a photo) recorded as a business expense."
  ).replace(
    "isExpense is true for anything billed on top of the work itself: travel, accommodation, per-diem, materials, software and subscriptions, training, certifications and exam fees, and reimbursements; false for the time, labour, consulting and services being invoiced.",
    "isExpense is false for the goods or services being invoiced — the licence, the equipment, the subscription, the course, the service itself; true only for incidental charges billed on top of them: freight, delivery, shipping, and card or payment surcharges. A fare, a flight or a room is the purchase itself."
  ) +
  `
13. expenseCategory is the one field that is a judgement rather than a transcription, and it is only a suggestion a person confirms; it changes nothing else in rule 1. Choose the category the purchase as a whole belongs to: SOFTWARE (licences, subscriptions, SaaS, hosting, cloud), HARDWARE (equipment, devices, peripherals), TRAVEL (flights, accommodation, transport), FOOD_AND_DRINKS (meals, coffee, catering, groceries, drinks — including while travelling), TRAINING (courses, certifications, exams, conferences), SUBCONTRACTOR (labour or services by another firm or person), PROFESSIONAL_FEES (legal, accounting, audit, consulting advice), OTHER (anything else). If the document does not make it clear, return UNKNOWN.`;

/**
 * What the model may answer for `expenseCategory`: a category, or UNKNOWN —
 * enums carry UNKNOWN instead of admitting null (the wire discipline above).
 */
export const EXPENSE_CATEGORY_READS = [...EXPENSE_CATEGORIES, "UNKNOWN"] as const;

/**
 * The expense read: the contractor schema plus `expenseCategory` (EXP-6). Its
 * own schema, not a field on the shared one — the contractor read is
 * transcription only, and would pay for a classification nobody uses.
 */
export const expenseExtractionSchema = z.strictObject({
  ...baseExtractionSchema.shape,
  expenseCategory: z.enum(EXPENSE_CATEGORY_READS),
});
export type ExpenseExtraction = z.infer<typeof expenseExtractionSchema>;

export const EXPENSE_EXTRACTION_SCHEMA_NAME = "expense_invoice_extraction";

export const EXPENSE_EXTRACTION_JSON_SCHEMA = {
  ...BASE_EXTRACTION_JSON_SCHEMA,
  properties: {
    ...BASE_EXTRACTION_JSON_SCHEMA.properties,
    expenseCategory: {
      type: "string",
      enum: [...EXPENSE_CATEGORY_READS],
      description: "The category the purchase as a whole belongs to — a suggestion, not a transcription.",
    },
  },
  required: [...BASE_EXTRACTION_JSON_SCHEMA.required, "expenseCategory"],
} as const;

/**
 * A stored expense reply, split into the read every engine shares and the
 * model's category suggestion.
 *
 * The split is what keeps the engines blind to the suggestion: `read` is a
 * plain `InvoiceExtraction`, byte-for-byte what a contractor read would parse
 * to, so the expense engine's input — and its version — does not change. Read
 * with the strict contractor parser instead, a reply carrying `expenseCategory`
 * would fail to parse and every new expense would lose its document facts.
 * Replies stored before the field existed read as no suggestion.
 */
export function parseStoredExpenseExtraction(
  content: string
): { read: InvoiceExtraction; expenseCategory: ExpenseCategory | null } | null {
  let value: unknown;
  try {
    value = JSON.parse(stripJsonFences(content));
  } catch {
    return null;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const parsed = expenseExtractionSchema.safeParse({ ...STORED_EXTRACTION_DEFAULTS, expenseCategory: "UNKNOWN", ...value });
  if (!parsed.success) return null;
  const { expenseCategory, ...read } = parsed.data;
  return { read, expenseCategory: expenseCategory === "UNKNOWN" ? null : expenseCategory };
}

/** The user turn that accompanies the PDF. */
export const INVOICE_EXTRACTION_USER_TEXT = "Extract the fields from this document.";

/**
 * The model's answer without the \`\`\` fences some models wrap JSON in despite
 * being told not to. One implementation, because the worker parses the answer and
 * the validator parses what the worker stored: a stored answer the second cannot
 * read loses the document facts, and a quote then reads as an invoice.
 */
export function stripJsonFences(content: string): string {
  const trimmed = content.trim();
  const match = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(trimmed);
  return match ? match[1]! : trimmed;
}
